import classNames from 'classnames'
import React, { useState } from 'react'
import ReactSelect, {
    ClearIndicatorProps,
    DropdownIndicatorProps,
    GroupBase,
    MenuListProps,
    MenuProps,
    MultiValueProps,
    OnChangeValue,
    OptionProps,
    components as ReactSelectComponents,
    SelectComponentsConfig,
    SingleValueProps,
    StylesConfig,
} from 'react-select'
import { Transition } from 'react-transition-group'

import { Button } from '../../button/button.js'
import { CheckIcon, ChevronDownLargeIcon, CloseIcon } from '../../icon/icon.js'
import { Spinner } from '../../spinner/spinner.js'
import { TextInputProps } from '../text-input.js'

export type SelectValue = string | string[] | null

export interface SelectOption {
    id?: string
    value: string
    label: string
}

export interface SelectProps extends Omit<TextInputProps, 'value'> {
    options: SelectOption[]
    className?: string
    isClearable?: boolean
    isLoading?: boolean
    live?: boolean
    loadingText?: string
    multiple?: boolean
    noResultsText?: string
    onChange?: (value: SelectValue) => void
    onHide?: () => void
    onShow?: () => void
    placeholder?: string
    value?: SelectValue
}

export const Select = (props: SelectProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const {
        bordered,
        elementRef,
        error,
        isClearable,
        isLoading,
        isRequired,
        label,
        labelClass,
        live,
        loadingText = 'Loading...',
        multiple,
        noResultsText,
        onChange,
        onHide,
        onShow,
        options,
        placeholder,
        ...wrapperProps
    } = props

    const { id, value, isDisabled, name } = wrapperProps

    const className = classNames('textfield select', {
        'select--bordered': bordered,
        'select--searchable': live,
        'select--placeholder': placeholder,
        'is-open': isOpen,
        'is-invalid': error,
        'is-disabled': isDisabled,
        'is-dirty': value,
    })

    const onMenuOpen = (): void => {
        if (!isOpen) {
            setIsOpen(true)
        }

        if (onShow) {
            onShow()
        }
    }

    const onMenuClose = (): void => {
        if (isOpen) {
            setIsOpen(false)

            if (onHide) {
                onHide()
            }
        }
    }

    const getDropDownIndicator = (indicatorProps: DropdownIndicatorProps<SelectOption, boolean>): JSX.Element => {
        const { children = <ChevronDownLargeIcon className="select__arrow" />, ...rest } = indicatorProps

        return <ReactSelectComponents.DropdownIndicator {...rest}>{children}</ReactSelectComponents.DropdownIndicator>
    }

    const menuContainerBEM = (transitionState?: string): string => {
        const classArray = ['select-menu-outer']

        if (transitionState) {
            classArray.push(`is-${transitionState}`)
        }

        if (isOpen) {
            classArray.push('is-shown')
        }

        return classArray.join(' ')
    }

    const getNoResultsText = (): string | null => {
        if (!noResultsText) {
            return null
        }

        return noResultsText
    }

    const getMenu = (menuProps: MenuProps<SelectOption, boolean>, menuIsOpen: boolean): JSX.Element => {
        const content = (state?: string) => (
            <ReactSelectComponents.Menu {...menuProps} className={menuContainerBEM(state)}>
                {menuProps.children}
            </ReactSelectComponents.Menu>
        )

        return (
            <Transition in={menuIsOpen} timeout={{ enter: 10, exit: 100 }} mountOnEnter={true} unmountOnExit={true}>
                {content}
            </Transition>
        )
    }

    const getMenuList = (menuListProps: MenuListProps<SelectOption, boolean>): JSX.Element => {
        return (
            <ReactSelectComponents.MenuList {...menuListProps}>{menuListProps.children}</ReactSelectComponents.MenuList>
        )
    }

    const isValueArray = (val: unknown): val is ReadonlyArray<SelectOption> => {
        return Array.isArray(val)
    }

    const getSimpleValue = (val: SelectOption | ReadonlyArray<SelectOption> | null): SelectValue => {
        if (!val) {
            return null
        }

        if (isValueArray(val)) {
            return val.map((v) => v.value)
        } else {
            return val.value
        }
    }

    const handleChange = (values?: OnChangeValue<SelectOption, boolean>): void => {
        if (!values || typeof values === 'undefined') {
            return
        }

        if (onChange) {
            onChange(getSimpleValue(values))
        }
    }

    const getOption = (optionProps: OptionProps<SelectOption, boolean>): JSX.Element => {
        const option: SelectOption = optionProps.data

        return (
            <ReactSelectComponents.Option {...optionProps}>
                <span className="select__option-label">{option.label}</span>
                <CheckIcon className="select__option-check" />
            </ReactSelectComponents.Option>
        )
    }

    const getSingleValue = (singleValueProps: SingleValueProps<SelectOption>): JSX.Element => {
        const singleValue: SelectOption = singleValueProps.data

        return (
            <ReactSelectComponents.SingleValue {...singleValueProps}>
                <div className="select__text-wrapper">{singleValue.label}</div>
            </ReactSelectComponents.SingleValue>
        )
    }

    const getMultiValue = (multiValueProps: MultiValueProps<SelectOption>): JSX.Element => {
        return (
            <ReactSelectComponents.MultiValue {...multiValueProps}>
                {multiValueProps.children}
            </ReactSelectComponents.MultiValue>
        )
    }

    const findSimpleValueFromOptions = (val: SelectValue, opts: SelectOption[]) => {
        if (Array.isArray(val)) {
            return opts.filter((object) => val.find((o) => o === object.value))
        } else {
            return opts.find((o) => o.value === value) || null
        }
    }

    const findDefaultValueFromOptions = (
        val: SelectValue,
        opts: SelectOption[],
    ): SelectOption | ReadonlyArray<SelectOption> | null => {
        if (!val) {
            return null
        }

        if (isValueArray(val)) {
            return opts.filter((object) => val.find((o) => o.value === object.value))
        } else {
            return opts.find((o) => o.value === val) || null
        }
    }

    const findValueFromOptions = (val: SelectValue | undefined, opts: SelectOption[]) => {
        if (typeof val === 'string' || (Array.isArray(val) && typeof val[0] === 'string')) {
            return findSimpleValueFromOptions(val as SelectValue, opts)
        } else {
            return findDefaultValueFromOptions(val as SelectValue, opts)
        }
    }

    const getValue = () => {
        return findValueFromOptions(value, options)
    }

    const getClearIndicator = (clearProps: ClearIndicatorProps<SelectOption, boolean>) => {
        const getClearButton = (): JSX.Element => (
            <Button
                className="select__clear"
                icon={CloseIcon}
                onClick={() => {
                    handleChange([])
                }}
                text="clear selection"
                type="button"
            />
        )

        const { children = getClearButton(), ...rest } = clearProps

        return <ReactSelectComponents.ClearIndicator {...rest}>{children}</ReactSelectComponents.ClearIndicator>
    }

    const getLoadingIndicator = () => <Spinner className="select__spinner" />

    const getLoadingMessage = () => {
        return <div className="select__loading-message">{loadingText}</div>
    }

    const renderReactSelect = () => {
        const customComponents: SelectComponentsConfig<SelectOption, boolean, GroupBase<SelectOption>> = {
            ClearIndicator: (clearProps) => getClearIndicator(clearProps),
            DropdownIndicator: (arrowProps) => getDropDownIndicator(arrowProps),
            IndicatorSeparator: () => null,
            Menu: (menuProps) => getMenu(menuProps, isOpen),
            MenuList: (menuProps) => getMenuList(menuProps),
            MultiValue: getMultiValue,
            MultiValueLabel: (menuProps) => <span>{menuProps.children}</span>,
            MultiValueRemove: () => null,
            LoadingIndicator: getLoadingIndicator,
            LoadingMessage: getLoadingMessage,
            Option: getOption,
            SingleValue: getSingleValue,
        }

        const styles: StylesConfig<SelectOption, false> = {
            container: () => ({}),
            control: () => ({}),
            indicatorsContainer: () => ({}),
            input: () => ({}),
            menu: () => ({}),
            menuList: () => ({}),
            menuPortal: (base, _props) => {
                base.zIndex = 31
                return base
            },
            multiValue: () => ({}),
            multiValueLabel: () => ({}),
            multiValueRemove: () => ({}),
            noOptionsMessage: () => ({}),
            option: () => ({}),
            placeholder: () => ({}),
            singleValue: () => ({}),
            valueContainer: () => ({}),
        }

        return (
            <ReactSelect
                id={id}
                instanceId={id}
                aria-labelledby={`${id}-label`}
                className="select__wrapper"
                name={name}
                options={options}
                value={getValue()}
                onChange={handleChange}
                classNamePrefix="select"
                components={customComponents}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                menuIsOpen={isOpen}
                styles={styles}
                isLoading={isLoading}
                isMulti={multiple}
                closeMenuOnSelect={!multiple}
                hideSelectedOptions={false}
                isDisabled={isDisabled}
                placeholder={placeholder || ''}
                menuPortalTarget={document.body}
                isClearable={isClearable}
                noOptionsMessage={getNoResultsText}
                backspaceRemovesValue={true}
                menuShouldScrollIntoView={true}
                isSearchable={!!live}
            />
        )
    }

    return (
        <div className={className} ref={elementRef}>
            <div className="select__inner">
                <label
                    className={classNames('textfield__label select__label', labelClass)}
                    id={`${id}-label`}
                    htmlFor={id}
                >
                    {label}
                    {isRequired && ' *'}
                </label>
                {renderReactSelect()}
            </div>
            {error && <div className="textfield__error select__error">{error}</div>}
        </div>
    )
}
